import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

/* ----------------------------- */
/* Helpers */
/* ----------------------------- */

const AVAILABILITY_MAP = {
  od9g9n9ve5xxywceoafg: "Private Collection"
};

const getAvailability = (image) => {
  let availability = image?.metadata?.availability;

  if (!availability) return null;

  if (Array.isArray(availability)) availability = availability[0];
  if (typeof availability === "object") availability = availability.value;

  if (AVAILABILITY_MAP[availability]) {
    return AVAILABILITY_MAP[availability];
  }

  return availability;
};

const shouldShowPrice = (image) => {
  const availability = (getAvailability(image) ?? "").toLowerCase().trim();
  return availability !== "private collection" && availability !== "sold";
};

const getPrice = (image) =>
  image?.metadata?.price ? `$${image.metadata.price}` : "Contact for price";

const trackImageView = (image) => {
  const dataLayer = (window.dataLayer = window.dataLayer || []);

  dataLayer.push({
    event: "image_view",
    image_folder: image.asset_folder,
    image_name: image?.metadata?.title,
    image_date: image.created_at,
    image_id: image.asset_id,
    image_availability: getAvailability(image),
    image_price: image?.metadata?.price
  });
};

/* ----------------------------- */
/* Metadata Component */
/* ----------------------------- */

const ImageMetadata = ({ image, showCollection }) => {
  const m = image?.metadata ?? {};

  return (
    <>
      <p><strong>{m.title ?? "Untitled"}</strong></p>

      <p>
        <strong>Dimensions: </strong>
        {m.width__inches_ ?? "Unknown"}" wide x{" "}
        {m.height__inches_ ?? "Unknown"}" tall
      </p>

      <p>
        <strong>Medium: </strong>
        {m.medium ?? "Unknown"}
      </p>

      <p>
        <strong>Availability: </strong>
        {getAvailability(image) ?? "Unknown"}
      </p>

      {shouldShowPrice(image) && (
        <p>
          <strong>Price: </strong>
          {getPrice(image)}
        </p>
      )}

      {showCollection && (
        <p>
          <strong>Collection: </strong>
          <Link to={`/collections/${image.asset_folder}`}>
            {image.asset_folder}
          </Link>
        </p>
      )}
    </>
  );
};

/* ----------------------------- */
/* Pagination Controls */
/* ----------------------------- */

const PaginationControls = ({ currentPage, totalPages, goPrev, goNext }) => (
  <div className="pagination-controls">
    <button onClick={goPrev} disabled={currentPage === 1}>
      Previous
    </button>

    <span>
      Page {currentPage} of {totalPages}
    </span>

    <button onClick={goNext} disabled={currentPage === totalPages}>
      Next
    </button>
  </div>
);

/* ----------------------------- */
/* Lightbox */
/* ----------------------------- */

const Lightbox = ({
  image,
  close,
  zoom,
  position,
  dragging,
  handlers,
  goNextImage,
  goPrevImage
}) => {
  if (!image) return null;

  return (
    <div
      className="lightbox-background"
      onMouseDown={handlers.handleMouseDown}
      onMouseMove={handlers.handleMouseMove}
      onMouseUp={handlers.handleMouseUp}
      onWheel={handlers.handleWheel}
    >
      <div className="lightbox-content">
        <button className="lightbox-prev" onClick={goPrevImage}>
          ‹
        </button>

        <button className="lightbox-next" onClick={goNextImage}>
          ›
        </button>

        <div
          className="lightbox-image-container"
          style={{
            overflow: "hidden",
            cursor: dragging ? "grabbing" : "grab"
          }}
        >
          <img
            className="lightbox-image"
            src={image?.url}
            alt={image?.description ?? ""}
            style={{
              transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
              transition: dragging ? "none" : "transform 0.3s ease"
            }}
          />
        </div>

        <div className="caption">
          <ImageMetadata image={image} showCollection={false} />
        </div>

        <span className="close" onClick={close}>
          &times;
        </span>

      </div>
    </div>
  );
};

/* ----------------------------- */
/* Main Component */
/* ----------------------------- */

function ImageGrid({
  imageData,
  imageCount,
  nextCursor,
  url,
  excludedFolders = [],
  excludedImages = [],
  isAllWorkPage
}) {

  const [images, setImages] = useState(imageData);
  const [cursor, setCursor] = useState(nextCursor);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 1100 : false
  );

  const itemsPerPage = 48;
  const totalPages = Math.ceil(imageCount / itemsPerPage);

  /* ----------------------------- */
  /* Resize detection */
  /* ----------------------------- */

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1100);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ----------------------------- */
  /* Fetch additional images */
  /* ----------------------------- */

  const fetchMoreImages = useCallback(async () => {
    if (!cursor) return;

    try {
      const response = await fetch(`${url}/${cursor}`);

      if (!response.ok) throw new Error("Fetch error");

      const data = await response.json();

      const fetchedImages = data.resources || data.images || [];

      /* Apply folder + image exclusions */

      const filteredImages = fetchedImages
        .filter(img => img.asset_id && !excludedImages.includes(img.asset_id))
        .filter(img =>
          img.asset_folder &&
          !excludedFolders.some(folder =>
            img.asset_folder.startsWith(folder)
          )
        );

      if (filteredImages.length > 0) {

        /* Prevent duplicates from cursor pagination */

        setImages(prev => {
          const combined = [...prev, ...filteredImages];

          const unique = Array.from(
            new Map(combined.map(img => [img.asset_id, img])).values()
          );

          return unique;
        });
      }

      setCursor(data.next_cursor || data.nextCursor);

    } catch (err) {
      console.error("Image fetch error:", err);
    }

  }, [cursor, url, excludedFolders, excludedImages]);

  /* ----------------------------- */
  /* Lightbox */
  /* ----------------------------- */

  const openLightbox = (image, index) => {
    if (isMobile) return;

    setSelectedImage(image);
    setSelectedIndex(index);

    setIsLightboxOpen(true);
    setZoom(1);
    setPosition({ x: 0, y: 0 });

    trackImageView(image);
    };


  const goNextImage = () => {
  if (selectedIndex === null) return;

  const nextIndex = (selectedIndex + 1) % currentItems.length;

  setSelectedIndex(nextIndex);
  setSelectedImage(currentItems[nextIndex]);

  trackImageView(currentItems[nextIndex]);
};

  const goPrevImage = () => {
    if (selectedIndex === null) return;

    const prevIndex =
      (selectedIndex - 1 + currentItems.length) % currentItems.length;

    setSelectedIndex(prevIndex);
    setSelectedImage(currentItems[prevIndex]);

    trackImageView(currentItems[prevIndex]);
  }; 

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
  };

  /* ----------------------------- */
  /* Pagination */
  /* ----------------------------- */

  const goNext = async () => {
    if (currentPage >= totalPages) return;

    setCurrentPage(p => p + 1);

    if (images.length < (currentPage + 1) * itemsPerPage) {
      await fetchMoreImages();
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrev = () => {
    if (currentPage <= 1) return;

    setCurrentPage(p => p - 1);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentItems = images.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  /* ----------------------------- */
  /* Zoom / drag handlers */
  /* ----------------------------- */

  const handleWheel = (event) => {
    setZoom(prev => Math.min(Math.max(prev + event.deltaY * -0.01, 1), 2));
  };

  const handleMouseDown = (event) => {
    setDragging(true);
    setStartPos({
      x: event.clientX - position.x,
      y: event.clientY - position.y
    });
  };

  const handleMouseMove = (event) => {
    if (!dragging) return;

    setPosition({
      x: event.clientX - startPos.x,
      y: event.clientY - startPos.y
    });
  };

  const handleMouseUp = () => setDragging(false);

  /* ----------------------------- */
  /* Render */
  /* ----------------------------- */

  return (
    <div>

      {nextCursor && (
        <div className="body-container">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            goPrev={goPrev}
            goNext={goNext}
          />
        </div>
      )}

      <div className="images-container">
        {currentItems.map((image, index) => (
          <div className="image-item" key={image.asset_id}>

            <img
              src={image.url}
              alt={image.description}
              onClick={() => openLightbox(image, index)}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "cover"
              }}
            />

            {!isMobile && (
              <>
                <p>{image?.metadata?.title ?? "Untitled"}</p>

                {isAllWorkPage && (
                  <p>
                    Collection:{" "}
                    <Link to={`/collections/${image.asset_folder}`}>
                      {image.asset_folder}
                    </Link>
                  </p>
                )}
              </>
            )}

            {isMobile && (
              <div className="image-metadata">
                <ImageMetadata
                  image={image}
                  showCollection={isAllWorkPage}
                />
              </div>
            )}

          </div>
        ))}
      </div>

      {isLightboxOpen && (
        <Lightbox
          image={selectedImage}
          close={closeLightbox}
          zoom={zoom}
          position={position}
          dragging={dragging}
          goNextImage={goNextImage}
          goPrevImage={goPrevImage}
          handlers={{
            handleWheel,
            handleMouseDown,
            handleMouseMove,
            handleMouseUp
          }}
        />
      )}

      {nextCursor && (
        <div className="body-container">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            goPrev={goPrev}
            goNext={goNext}
          />
        </div>
      )}

    </div>
  );
}

export default ImageGrid;